import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  CheckCircle,
  Clock,
  GraduationCap,
  PlayCircle,
  Users,
} from "lucide-react";
import { cn } from "@/utils/utils";
import { CatalogItem } from "@/modules/learningCenter/features/CourseDirectory/api/getCatalog";
import { levelBadgeClasses } from "@/modules/learningCenter/features/CourseDirectory/utils/level";

interface CourseCardProps {
  course: CatalogItem;
}

// Own-enrollment-status overlay. `none` renders no overlay. The catalog item
// does not carry a per-course progress percentage, so in-progress shows a "%"
// glyph alongside its icon to signal mid-course state without a fabricated
// number.
function StatusOverlay({
  status,
}: {
  status: CatalogItem["enrollmentStatus"];
}) {
  if (status === "completed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-0.5 text-xs font-medium text-white shadow-sm">
        <CheckCircle className="h-3 w-3" />
        Completed
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-2.5 py-0.5 text-xs font-medium text-white shadow-sm">
        <PlayCircle className="h-3 w-3" />
        In Progress
      </span>
    );
  }
  if (status === "enrolled") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-600 px-2.5 py-0.5 text-xs font-medium text-white shadow-sm">
        <PlayCircle className="h-3 w-3" />
        Enrolled
      </span>
    );
  }
  return null;
}

export function CourseCard({ course }: CourseCardProps) {
  return (
    <Link
      className="block group"
      to={`/hub/learning-center/courses/${course.uuid}`}
    >
      <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 transition-colors group-hover:border-gray-300 dark:group-hover:border-slate-600">
        {/* Thumbnail — real image when present, otherwise a calm neutral
            placeholder with a single muted icon. */}
        <div className="relative aspect-video overflow-hidden">
          {course.thumbnailUrl ? (
            <img
              alt={course.title}
              className="block h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              src={course.thumbnailUrl}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-slate-900">
              <GraduationCap
                className="h-9 w-9 text-gray-300 dark:text-slate-700"
                strokeWidth={1.5}
              />
            </div>
          )}

          {/* Own-status overlay */}
          <div className="absolute right-2.5 top-2.5">
            <StatusOverlay status={course.enrollmentStatus} />
          </div>
        </div>

        <div className="flex flex-1 flex-col p-4">
          {course.difficulty && (
            <span
              className={cn(
                "mb-2 inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize",
                levelBadgeClasses(course.difficulty),
              )}
            >
              {course.difficulty}
            </span>
          )}

          <h3 className="text-[15px] font-medium leading-snug text-gray-900 dark:text-white line-clamp-2">
            {course.title}
          </h3>

          {course.tagline && (
            <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">
              {course.tagline}
            </p>
          )}

          <div className="mt-auto flex items-center gap-4 border-t border-gray-100 dark:border-slate-700/50 pt-3 text-xs text-gray-500 dark:text-gray-400">
            {course.estimatedHours && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {course.estimatedHours} hrs
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5" />
              {course.moduleCount}{" "}
              {course.moduleCount === 1 ? "module" : "modules"}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {course.enrollmentCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
